import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getAllFinancialYears, getBindFDRBanks, getBankFDRNos, getFDReportDataWithBalance } from '../../api/ReportsAPI/fdReportAPI';

const err = (e, fallback) => e?.Message || e?.message || fallback;

export const fetchFDFinancialYears = createAsyncThunk('fdReport/fetchYears', async (_, { rejectWithValue }) => {
    try { const res = await getAllFinancialYears(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch financial years')); }
});

export const fetchFDBankList = createAsyncThunk('fdReport/fetchBankList', async (fdrno, { rejectWithValue }) => {
    try { const res = await getBindFDRBanks(fdrno); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch bank list')); }
});

export const fetchFDNumbers = createAsyncThunk('fdReport/fetchFDNumbers', async (bankId, { rejectWithValue }) => {
    try { const res = await getBankFDRNos(bankId); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch FD numbers')); }
});

export const fetchFDReport = createAsyncThunk('fdReport/fetchReport', async (params, { rejectWithValue }) => {
    try { const res = await getFDReportDataWithBalance(params); return res?.Data || null; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch FD report')); }
});

const initialState = {
    years: [], yearsLoading: false,
    bankList: [], bankListLoading: false,
    fdNumbers: [], fdNumbersLoading: false,
    report: null, reportLoading: false, reportError: null,
};

const fdReportSlice = createSlice({
    name: 'fdReport',
    initialState,
    reducers: {
        clearFDReport: (s) => { s.report = null; s.reportError = null; },
        clearFDNumbers: (s) => { s.fdNumbers = []; },
        resetFDReport: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchFDFinancialYears.pending, (s) => { s.yearsLoading = true; })
            .addCase(fetchFDFinancialYears.fulfilled, (s, a) => { s.yearsLoading = false; s.years = a.payload; })
            .addCase(fetchFDFinancialYears.rejected, (s) => { s.yearsLoading = false; });

        builder
            .addCase(fetchFDBankList.pending, (s) => { s.bankListLoading = true; })
            .addCase(fetchFDBankList.fulfilled, (s, a) => { s.bankListLoading = false; s.bankList = a.payload; })
            .addCase(fetchFDBankList.rejected, (s) => { s.bankListLoading = false; });

        builder
            .addCase(fetchFDNumbers.pending, (s) => { s.fdNumbersLoading = true; })
            .addCase(fetchFDNumbers.fulfilled, (s, a) => { s.fdNumbersLoading = false; s.fdNumbers = a.payload; })
            .addCase(fetchFDNumbers.rejected, (s) => { s.fdNumbersLoading = false; });

        builder
            .addCase(fetchFDReport.pending, (s) => { s.reportLoading = true; s.reportError = null; })
            .addCase(fetchFDReport.fulfilled, (s, a) => { s.reportLoading = false; s.report = a.payload; })
            .addCase(fetchFDReport.rejected, (s, a) => { s.reportLoading = false; s.reportError = a.payload; });
    },
});

export const { clearFDReport, clearFDNumbers, resetFDReport } = fdReportSlice.actions;

export const selectFDYears            = (s) => s.fdReport.years;
export const selectFDYearsLoading     = (s) => s.fdReport.yearsLoading;
export const selectFDBankList         = (s) => s.fdReport.bankList;
export const selectFDBankListLoading  = (s) => s.fdReport.bankListLoading;
export const selectFDNumbers          = (s) => s.fdReport.fdNumbers;
export const selectFDNumbersLoading   = (s) => s.fdReport.fdNumbersLoading;
export const selectFDReport           = (s) => s.fdReport.report;
export const selectFDReportLoading    = (s) => s.fdReport.reportLoading;
export const selectFDReportError      = (s) => s.fdReport.reportError;

export default fdReportSlice.reducer;
